import React, { createContext, useContext, useState } from "react";
import axios from "axios";
import { API_BASE_URL, CLASS_PREMIUM, CLASS_PREMIUM_PLUS } from "@config/config";
import { useSearchOptions } from "@queries/useSearchOptions";
import { AuthContext } from "@context/AuthContext";
import { getSessionType } from "@utils/ticketUtils";
import { buildSessionSearchParams } from "@utils/urlUtils";

export const SearchContext = createContext({});

const SearchContextProvider = (props) => {
    const { isLoggedIn } = useContext(AuthContext);
    const [venue, setVenue] = useState({});
    const [movie, setMovie] = useState({});

    const [venueName, setVenueName] = useState("");
    const [experienceType, setExperienceType] = useState(null);
    const [selectedExperiences, setSelectedExperiences] = useState([]);
    const [isLoadingSessions, setIsLoadingSessions] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [primarySessions, setPrimarySessions] = useState({});
    const [secondarySessions, setSecondarySessions] = useState({});
    const [availableDates, setAvailableDates] = useState([]);
    const [availableClasses, setAvailableClasses] = useState([]);
    const [sessionsByVenue, setSessionsByVenue] = useState({});

    const [searchEnabled, setSearchEnabled] = useState(false);
    const { searchOptions, isSearchOptionsLoading } = useSearchOptions(searchEnabled);
    const fetchSearchOptions = () => setSearchEnabled(true);



    const { children } = props;

    const fetchMovieSessions = ({
                                    venueName,
                                    movieTitle,
                                    sessionDate,
                                    lat,
                                    lng,
                                    ticketClassId,
                                    search_distance = 25,
                                } = {}) => {

        setIsLoadingSessions(true);

        const params = buildSessionSearchParams({
            venueName,
            movieTitle,
            sessionDate,
            lat,
            lng,
            ticket_class_id: ticketClassId,
            search_distance,
        });

        const queryString = params.toString();
        const endpoint     = queryString
            ? `${API_BASE_URL}/session?${queryString}`
            : `${API_BASE_URL}/session`;
        axios.get(endpoint)
            .then((response) => {
                const sessionData = response.data.data.map((item) => {
                    const sessionType = getSessionType(item);
                    return {
                        ...item,
                        experience_type: sessionType,
                        venue_image: item.venue_image,
                        ticket_price_cents: parseInt(item.ticket_price_cents, 10),
                        rrp_pricing_cents: item.rrp_pricing_cents,
                    };
                });
                setSessions(sessionData);
            })
            .catch((error) => {
                console.log(error);
                setSessions([]);
                if (isLoggedIn && error?.response?.status === 401) {
                    window.location = "/";
                }
            })
            .finally(() => {
                setIsLoadingSessions(false);
            });
    };


    const fetchMovie = async (id, movieName) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/movie/${id}/${movieName}`);
            if (response.status === 200) {
                const movieData = response.data.data.movie;
                setMovie(movieData);
                return movieData;
            }
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    const fetchSessionsByMovie = ({
                                      venueName,
                                      movieId,
                                      sessionDate,
                                      lat,
                                      lng,
                                      ticketClassId,
                                      search_distance = 25,
                                  } = {}) => {

        setIsLoadingSessions(true);

        const params = buildSessionSearchParams({
            venueName,
            movieId,
            sessionDate,
            lat,
            lng,
            ticket_class_id: ticketClassId,
            search_distance,
        });

        const queryString = params.toString();
        const endpoint = queryString
            ? `${API_BASE_URL}/sessions?${queryString}`
            : `${API_BASE_URL}/sessions`;

        return axios.get(endpoint)
            .then((response) => {
                const mergedAvailableClasses = mergeAvailableClasses(response.data.availableClasses);
                setPrimarySessions(response.data.primarySessions);
                setSecondarySessions(response.data.secondarySessions);
                setAvailableDates(response.data.availableDates);
                setAvailableClasses(mergedAvailableClasses);
                return response.data;
            })
            .catch((error) => {
                console.log(error);
                setPrimarySessions({});
                setSecondarySessions({});
                setAvailableDates([]);
                setAvailableClasses([]);
                if (isLoggedIn && error?.response?.status === 401) {
                    window.location = "/";
                }
            })
            .finally(() => {
                setIsLoadingSessions(false);
            });
    };

    // Helper to merge Premium ticket class id and Premium Plus ticket class id into one option
    const mergeAvailableClasses = (classes) => {
        if (!classes || classes.length === 0) return [];

        const hasPremium = classes.some(c => c.id === CLASS_PREMIUM);
        const hasPremiumPlus = classes.some(c => c.id === CLASS_PREMIUM_PLUS);
        const filtered = classes.filter(c => c.id !== CLASS_PREMIUM_PLUS);
        // If only Premium Plus exists (no Premium), show Prem plus as Premium
        if (!hasPremium && hasPremiumPlus) {
            const premiumPlus = classes.find(c => c.id === CLASS_PREMIUM_PLUS);
            if (premiumPlus) {
                filtered.push({
                    ...premiumPlus,
                    id: CLASS_PREMIUM,
                    short_name: "PREM",
                    long_name: "Premium Standard",
                });
            }
        }

        return filtered;
    };

    return (
        <SearchContext.Provider
            value={{
                venue,
                movie,
                fetchMovie,
                venueName,
                setVenueName,
                setVenue,
                setSessionsByVenue,
                setAvailableDates,
                setAvailableClasses,
                fetchSearchOptions,
                isSearchOptionsLoading,
                isLoadingSessions,
                setIsLoadingSessions,
                searchOptions,
                sessions,
                setSessions,
                sessionsByVenue,
                experienceType,
                setExperienceType,
                selectedExperiences,
                setSelectedExperiences,
                setMovie,
                fetchMovieSessions,
                fetchSessionsByMovie,
                primarySessions,
                secondarySessions,
                availableDates,
                availableClasses,
            }}
        >
            {children}
        </SearchContext.Provider>
    );
};

export default SearchContextProvider;
